import { motion } from 'framer-motion';
import { useRegularProjects } from '../hooks/useProjects';
import { useTickets } from '../hooks/useTickets';
import { useUserStore } from '../store/userStore';
import { DashboardHeader } from '../components/DashboardHeader';
import { RiskCard } from '../components/RiskCard';
import { VelocityPanel } from '../components/VelocityPanel';
import type { Project, Ticket } from '../types';

function ProjectSkeleton() {
  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="flex justify-between items-start">
        <div className="skeleton h-5 w-36 rounded" />
        <div className="skeleton h-8 w-12 rounded" />
      </div>
      <div className="skeleton h-3 w-full rounded" />
      <div className="skeleton h-3 w-4/5 rounded" />
    </div>
  );
}

function BlockersSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-lg p-3 space-y-1.5"
        >
          <div className="skeleton h-4 w-3/4 rounded" />
          <div className="skeleton h-3 w-1/2 rounded" />
        </div>
      ))}
    </div>
  );
}

function ProjectWorkload({ project }: { project: Project }) {
  const { data: tickets = [] } = useTickets(project.projectId);

  const assigned = tickets.filter((t) => t.assigneeId && t.status !== 'complete' && t.status !== 'invalid');
  if (assigned.length === 0) return null;

  const totalCount = assigned.length;
  const totalEstimate = assigned.reduce((s, t) => s + (t.estimate ?? 1), 0);
  const totalPriority = assigned.reduce((s, t) => s + (t.priority ?? 1), 0);

  // Group by assignee
  const byAssignee = new Map<string, { name: string; count: number; estimate: number; priority: number }>();
  for (const t of assigned) {
    const key = t.assigneeId!;
    if (!byAssignee.has(key)) {
      byAssignee.set(key, { name: t.assigneeName ?? 'Unassigned', count: 0, estimate: 0, priority: 0 });
    }
    const entry = byAssignee.get(key)!;
    entry.count += 1;
    entry.estimate += t.estimate ?? 1;
    entry.priority += t.priority ?? 1;
  }

  const sorted = [...byAssignee.values()].sort((a, b) => b.estimate - a.estimate);

  return (
    <div>
      <p className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
        {project.name}
      </p>
      <div className="space-y-4">
        {sorted.map(({ name, count, estimate, priority }) => {
          const countPct = Math.round((count / totalCount) * 100);
          const estPct = Math.round((estimate / totalEstimate) * 100);
          const sigPct = Math.round((priority / totalPriority) * 100);
          return (
            <div key={name}>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-meta text-gray-900 dark:text-white font-medium truncate flex-1 mr-2">{name}</span>
                <div className="flex items-center gap-2 flex-shrink-0 text-meta">
                  <span className="text-gray-500 dark:text-gray-400">{countPct}% tickets</span>
                  <span className="text-blue-500 dark:text-blue-400 font-semibold">{estPct}% load</span>
                  <span className="text-amber-500 dark:text-amber-400 font-semibold">{sigPct}% sig</span>
                </div>
              </div>
              {/* Stacked bar: ticket count (gray), estimate load (blue), significance (amber) */}
              <div className="h-2 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden flex gap-px">
                <div className="h-full bg-gray-300 dark:bg-white/20 rounded-l-full" style={{ width: `${countPct}%` }} />
                <div className="h-full bg-blue-400" style={{ width: `${estPct}%` }} />
                <div className="h-full bg-amber-400 rounded-r-full" style={{ width: `${sigPct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AtRiskTicketsList({ project }: { project: Project }) {
  const { data: tickets, isLoading } = useTickets(project.projectId, {
    status: 'at_risk',
  });

  if (isLoading) {
    return (
      <div className="skeleton h-8 rounded w-full" />
    );
  }

  if (!tickets || tickets.length === 0) return null;

  return (
    <>
      {tickets.map((ticket: Ticket) => (
        <div
          key={ticket.ticketId}
          className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06]"
        >
          <div className="min-w-0">
            <p className="text-title text-gray-900 dark:text-white truncate">
              {ticket.title}
            </p>
            <p className="text-meta text-gray-500 dark:text-gray-400">
              {project.name}
            </p>
          </div>
          <span className="flex-shrink-0 text-meta font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-900/40">
            At Risk
          </span>
        </div>
      ))}
    </>
  );
}

export function TeamLeadDashboard({ onBackToToday }: { onBackToToday?: () => void } = {}) {
  const { sessionConfig } = useUserStore();
  const { data: projects, isLoading: projectsLoading } = useRegularProjects();

  const regularProjects = projects ?? [];

  // Pick the first at-risk project, or the one with the highest risk score
  const featuredProject =
    regularProjects.find((p) => p.status === 'at_risk') ??
    regularProjects.reduce<Project | null>((best, p) => {
      if (p.overallRiskScore === null) return best;
      if (best === null || best.overallRiskScore === null) return p;
      return p.overallRiskScore > best.overallRiskScore ? p : best;
    }, null);

  return (
    <div className="px-6 py-6 sm:px-10 lg:px-16">
      <DashboardHeader title="Team Dashboard" roleLabel="Team Lead" onBackToToday={onBackToToday} />

      {featuredProject && (
        <div className="mb-6">
          <VelocityPanel projectId={featuredProject.projectId} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sprint Health — Your Projects */}
        <div className="lg:col-span-2">
          <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6">
            Your Projects
          </h2>
          {projectsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(4)].map((_, i) => (
                <ProjectSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {regularProjects.map((project) => (
                <RiskCard key={project.projectId} project={project} />
              ))}
              {regularProjects.length === 0 && (
                <p className="text-body font-medium text-gray-500 col-span-full py-8 text-center">
                  No projects found.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Team Blockers */}
          <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5">
            <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6">
              Needs Attention
            </h2>
            {projectsLoading ? (
              <BlockersSkeleton />
            ) : (
              <div className="space-y-4">
                {regularProjects.map((project) => (
                  <AtRiskTicketsList key={project.projectId} project={project} />
                ))}
                {regularProjects.length === 0 && (
                  <p className="text-meta text-gray-500">No projects to check.</p>
                )}
              </div>
            )}
          </div>

          {/* Workload Balance */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5"
          >
            <div className="mb-3">
              <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Workload Balance
              </h2>
              <p className="text-meta text-gray-400 dark:text-gray-500 mt-0.5">
                <span className="text-gray-400">▪ tickets</span>
                {' · '}
                <span className="text-blue-400">▪ load (complexity)</span>
                {' · '}
                <span className="text-amber-400">▪ significance</span>
              </p>
            </div>
            {projectsLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-1">
                    <div className="skeleton h-3 w-1/2 rounded" />
                    <div className="skeleton h-2 w-full rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-5">
                {regularProjects.map((project) => (
                  <ProjectWorkload key={project.projectId} project={project} />
                ))}
                {regularProjects.length === 0 && (
                  <p className="text-meta text-gray-500">No projects.</p>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
